import { useEffect, useMemo, useRef, useState } from 'preact/hooks';

import { WIDGET_KIND_DRAG_TYPE } from '../dragTypes';
import { WidgetHost } from '../../WidgetHost';
import type { IPointBindingSource } from '../../binding';
import type { WidgetDefinition } from '../../widgets';
import type { InMemoryPointBindingSource } from '../../InMemorySource';
import { createPalettePreviewSource } from './previewSource';
import {
  THUMB_H,
  THUMB_W,
  THUMB_W_WIDE,
  TILE_GAP,
  isWide,
  thumbGeometry,
} from './thumbGeometry';
import { PALETTE_ITEMS, PALETTE_TABS, itemHaystack, type PaletteItem } from './paletteItems';
import { S400_PARTS, s400DemoKey } from '../../parts/s400/index';
import './WidgetPalette.css';

/**
 * Der MIME-Typ, unter dem die Palette den Eintrag in den Ziehvorgang legt.
 *
 * Eigener Typ und nicht `text/plain`: die Rasterflaeche nimmt genau diesen
 * entgegen und weist alles andere ab. Ein aus dem Browser hereingezogener
 * Text oder eine Datei sollen nicht versehentlich ein Widget erzeugen. Muster
 * dafuer steht in `pages/PlantSchemaPage.tsx`, dort heisst der Typ
 * `application/x-hvacloud-schema`.
 *
 * Getragen wird der Schluessel des Eintrags (paletteItems.ts), fuer die
 * gewoehnlichen Widgets ist das die Art selbst.
 */
export { WIDGET_KIND_DRAG_TYPE as WIDGET_KIND_MIME } from '../dragTypes';

/*
 * Die Masse und die Rechnung der Miniatur stehen in `thumbGeometry.ts`, samt
 * Begruendung fuer `transform: scale()`. Sie sind von dort auch geprueft;
 * hier wird nur angewendet.
 */

export interface WidgetPaletteProps {
  /**
   * Klick-Weg. Die Palette legt selbst nichts am Projekt an, sie kennt den
   * Store nicht: sie sagt nur, was der Autor genommen hat, und die Seite
   * entscheidet, wo es hinkommt.
   *
   * Beide Wege werden gebraucht. Ziehen ist der natuerliche, wenn die
   * Zielstelle sichtbar ist. Klicken ist der einzige, der auf einem
   * Bedienpanel mit Finger funktioniert und der einzige, der noch geht, wenn
   * die Zielstelle gerade ausserhalb des Sichtbereichs liegt.
   */
  onPick(item: PaletteItem): void;
  /** Optionaler Klassenhaken fuer das Layout der Seite. */
  class?: string;
}

/** Reiter "Alle": der fruehere Zustand, eine Liste ueber alles. */
const ALL_TAB = 'alle';
const TAB_STORAGE = 'hv-hmi-palette-tab';

function readTab(): string {
  try {
    const t = localStorage.getItem(TAB_STORAGE);
    if (t && (t === ALL_TAB || PALETTE_TABS.some((x) => x.key === t))) return t;
  } catch { /* ohne Speicher: Vorgabe */ }
  return PALETTE_TABS[0]!.key;
}

/**
 * Beispielwerte fuer die Teilschemen. Temperaturen nach der Farbe des
 * Felds (warm oben, kalt unten), Pumpen laufen, damit die Miniatur zeigt,
 * wie das Teilschema im Betrieb aussieht.
 */
function seedS400(source: InMemoryPointBindingSource): void {
  for (const part of S400_PARTS) {
    part.fields.forEach((f, i) => {
      const key = s400DemoKey(f.id);
      if (f.type === 'schalter') source.seed(key, true);
      else if (f.type === 'text') source.seed(key, 'Auto');
      else if (f.unit === '°C') {
        const base = f.tone === 'cool' ? 36 : f.tone?.startsWith('warm') ? 58 : f.setpoint ? 55 : 21;
        source.seed(key, base + ((i * 7) % 10) / 10);
      } else source.seed(key, 42 + i);
    });
  }
}

export function WidgetPalette(props: WidgetPaletteProps) {
  const [query, setQuery] = useState('');
  const [tab, setTabState] = useState(readTab);
  /**
   * Die zugeklappten Abschnitte, nicht die aufgeklappten.
   *
   * Herum gedacht, und das ist Absicht: neu ist alles offen. Wer eine
   * Gruppe nie anfasst, soll sie trotzdem gesehen haben.
   */
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(new Set());
  const scrollRef = useRef<HTMLDivElement | null>(null);

  function setTab(next: string): void {
    setTabState(next);
    try { localStorage.setItem(TAB_STORAGE, next); } catch { /* egal */ }
    scrollRef.current?.scrollTo({ top: 0 });
  }

  /**
   * Eine Quelle fuer alle Miniaturen, angelegt beim ersten Rendern.
   *
   * Sie haelt nur Werte und Ringpuffer, keine Verbindung: das Abraeumen
   * besteht darin, dass die Widgets ihre Abos beim Abhaengen schliessen,
   * und das tun sie selbst.
   */
  const source = useMemo(() => {
    const s = createPalettePreviewSource();
    seedS400(s);
    return s;
  }, []);

  /**
   * Die Beispiel-Widgets, einmal erzeugt.
   *
   * `preview()` liefert bei jedem Aufruf ein frisches Objekt. Bei jedem
   * Rendern neu aufgerufen bekaeme jedes Widget eine neue Definition in die
   * Hand, und die Renderer, die auf die Identitaet ihrer Definition achten
   * (Anlagenschema, Trend), baeuten ihre Abos jedes Mal neu auf.
   */
  const previews = useMemo(() => {
    const out = new Map<string, WidgetDefinition | null>();
    for (const item of PALETTE_ITEMS) out.set(item.key, item.preview());
    return out;
  }, []);

  const haystacks = useMemo(() => new Map(PALETTE_ITEMS.map((i) => [i.key, itemHaystack(i)])), []);
  const needle = query.trim().toLowerCase();
  const searching = needle !== '';

  const counts = useMemo(() => {
    const c = new Map<string, number>();
    for (const i of PALETTE_ITEMS) c.set(i.tab, (c.get(i.tab) ?? 0) + 1);
    return c;
  }, []);

  /** Abschnitte im sichtbaren Reiter. Waehrend einer Suche ueber alle
   *  Reiter, sonst sucht man im falschen und findet nichts. Mehrere Woerter
   *  muessen alle vorkommen: "puffer solar". */
  const sections = useMemo(() => {
    const words = needle.split(/\s+/).filter(Boolean);
    const items = PALETTE_ITEMS.filter((i) => (searching || tab === ALL_TAB || i.tab === tab)
      && words.every((w) => haystacks.get(i.key)!.includes(w)));
    const out: { key: string; label: string; items: PaletteItem[] }[] = [];
    for (const item of items) {
      const key = `${item.tab}:${item.section}`;
      let sec = out.find((s) => s.key === key);
      if (!sec) { sec = { key, label: item.section, items: [] }; out.push(sec); }
      sec.items.push(item);
    }
    return out;
  }, [needle, searching, tab, haystacks]);

  const empty = sections.length === 0;
  const hits = sections.reduce((n, s) => n + s.items.length, 0);

  function toggleSection(key: string): void {
    setCollapsed((current) => {
      const next = new Set(current);
      if (!next.delete(key)) next.add(key);
      return next;
    });
  }

  const tabs = [{ key: ALL_TAB, label: 'Alle', hint: 'Alle Bausteine untereinander' }, ...PALETTE_TABS];

  return (
    <nav
      class={'hv-wp' + (props.class ? ` ${props.class}` : '')}
      aria-label="Bausteine"
      style={{
        '--hv-wp-thumb-w': `${THUMB_W}px`,
        '--hv-wp-thumb-h': `${THUMB_H}px`,
        '--hv-wp-thumb-w-wide': `${THUMB_W_WIDE}px`,
        '--hv-wp-tile-gap': `${TILE_GAP}px`,
      }}
    >
      <div class="hv-wp__search">
        <input
          class="hv-wp__search-input"
          type="search"
          value={query}
          placeholder="Suchen, etwa Puffer oder S3K"
          aria-label="Bausteine durchsuchen"
          onInput={(e) => setQuery((e.currentTarget as HTMLInputElement).value)}
          onKeyDown={(e) => { if (e.key === 'Escape') setQuery(''); }}
        />
      </div>

      <div class="hv-wp__tabs" role="tablist" aria-label="Kategorien">
        {tabs.map((t) => {
          const active = !searching && t.key === tab;
          const n = t.key === ALL_TAB ? PALETTE_ITEMS.length : counts.get(t.key) ?? 0;
          return (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={active}
              class={`hv-wp__tab${active ? ' is-active' : ''}`}
              title={t.hint}
              onClick={() => { setQuery(''); setTab(t.key); }}
            >
              {t.label}
              <span class="hv-wp__tab-count">{n}</span>
            </button>
          );
        })}
      </div>

      <div class="hv-wp__scroll" ref={scrollRef}>
        {searching && !empty && (
          <p class="hv-wp__hint">{hits} Treffer in allen Kategorien</p>
        )}
        {empty && (
          <p class="hv-wp__empty">Kein Baustein passt zu „{query}“.</p>
        )}
        {sections.map((section) => {
          // Waehrend einer Suche zaehlt das Zuklappen nicht. Ein Treffer
          // hinter einem zugeklappten Abschnitt ist kein Treffer, sondern
          // ein Raetsel.
          const open = searching || !collapsed.has(section.key);
          return (
            <section class="hv-wp__group" key={section.key}>
              <h4 class="hv-wp__group-head">
                <button
                  type="button"
                  class="hv-wp__group-toggle"
                  aria-expanded={open}
                  onClick={() => toggleSection(section.key)}
                >
                  <span class={`hv-wp__chevron${open ? ' hv-wp__chevron--open' : ''}`} aria-hidden="true" />
                  <span class="hv-wp__group-label">{section.label}</span>
                  <span class="hv-wp__group-count">{section.items.length}</span>
                </button>
              </h4>
              {open && (
                <ul class="hv-wp__grid">
                  {section.items.map((item) => (
                    <li
                      key={item.key}
                      class={'hv-wp__cell' + (isWide(item) ? ' hv-wp__cell--wide' : '')}
                    >
                      <PaletteTile
                        item={item}
                        preview={previews.get(item.key) ?? null}
                        source={source}
                        onPick={props.onPick}
                      />
                    </li>
                  ))}
                </ul>
              )}
            </section>
          );
        })}
      </div>
    </nav>
  );
}

/**
 * Eine Kachel: Miniatur, Name, Rastermass oder Kurzzeichen.
 *
 * KEIN `<button>`, obwohl es einer war. In der Miniatur stehen echte
 * Widgets mit eigenen Knoepfen, Schiebereglern und Eingabefeldern. Ein
 * Knopf im Knopf ist ungueltiges HTML, und der Browser haengt die
 * verschachtelten Elemente beim Auswerten aus dem Knopf heraus. Die Kachel
 * ist deshalb ein `div` mit `role="button"` und eigener Tastaturbehandlung.
 */
function PaletteTile(props: {
  item: PaletteItem;
  preview: WidgetDefinition | null;
  source: IPointBindingSource;
  onPick(item: PaletteItem): void;
}) {
  const { item } = props;

  function pick(): void {
    props.onPick(item);
  }

  return (
    <div
      class="hv-wp__item"
      role="button"
      tabIndex={0}
      draggable
      title={`${item.title}: ${item.description}`}
      onClick={pick}
      onKeyDown={(e) => {
        // Enter und Leertaste sind das, was ein Knopf von sich aus koennte.
        // Beim Leerzeichen muss das Blaettern unterdrueckt werden, sonst
        // springt die Palette beim Auswaehlen nach unten.
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          pick();
        }
      }}
      onDragStart={(e) => {
        if (!e.dataTransfer) return;
        e.dataTransfer.effectAllowed = 'copy';
        e.dataTransfer.setData(WIDGET_KIND_DRAG_TYPE, item.key);
        // Zusaetzlich als Klartext, damit ein Ziehvorgang, der versehentlich
        // in einem Textfeld endet, dort wenigstens etwas Lesbares ablegt.
        e.dataTransfer.setData('text/plain', item.key);
      }}
    >
      <Thumbnail item={item} preview={props.preview} source={props.source} />
      <span class="hv-wp__item-title">{item.title}</span>
      <span class="hv-wp__item-size" aria-hidden="true">
        {item.code ?? <>{item.colSpan}&times;{item.rowSpan}</>}
      </span>
    </div>
  );
}

/**
 * Die Miniatur: das echte Widget, klein und bedienungsblind.
 *
 * Gerendert wird erst, wenn die Kachel ins Blickfeld kommt
 * ({@link useInView}). Alle Widgets auf einmal waeren nicht nur viele
 * Baeume, sondern auch ECharts-Instanzen und Hunderte Abos, und das alles
 * in dem Moment, in dem der Autor den Editor oeffnet und eigentlich sein
 * Bild sehen will.
 */
function Thumbnail(props: {
  item: PaletteItem;
  preview: WidgetDefinition | null;
  source: IPointBindingSource;
}) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const seen = useInView(hostRef);
  const { item, preview } = props;
  const geometry = thumbGeometry(item);

  return (
    <div
      class={'hv-wp__thumb' + (isWide(item) ? ' hv-wp__thumb--wide' : '')}
      aria-hidden="true"
      /*
       * Bedienungsblind auf zwei Wegen, und beide werden gebraucht.
       *
       * `inert` nimmt die Knoepfe und Regler in der Miniatur aus der
       * Tabulatorreihenfolge und aus dem Bedienungsbaum. Das Stylesheet setzt
       * zusaetzlich `pointer-events: none`, weil `inert` in aelteren
       * Browsern schlicht ignoriert wird und ein Klick auf die Palette
       * sonst etwas an der Anlage schaltet, statt ein Widget aufzunehmen.
       *
       * Als Attribut ueber den Verweis gesetzt und nicht als Eigenschaft
       * im JSX: die Typen von Preact kennen `inert` nicht.
       */
      ref={(el) => {
        hostRef.current = el;
        el?.setAttribute('inert', '');
      }}
    >
      {preview === null ? (
        <span class="hv-wp__thumb-none">Braucht ein Kunden-SVG</span>
      ) : seen ? (
        <div
          class="hv-wp__stage"
          style={{
            width: `${geometry.nativeW}px`,
            height: `${geometry.nativeH}px`,
            transform: `scale(${geometry.scale})`,
            left: `${geometry.left}px`,
            top: `${geometry.top}px`,
          }}
        >
          <WidgetHost widget={preview} source={props.source} />
        </div>
      ) : (
        <span class="hv-wp__thumb-wait" />
      )}
    </div>
  );
}

/**
 * War dieses Element schon einmal im Blickfeld?
 *
 * Einmal wahr, immer wahr: wer zurueckscrollt, soll keine Miniatur neu
 * aufbauen sehen, und ein Widget, das beim Vorbeiscrollen laufend ab- und
 * angehaengt wird, kostet mehr als es spart.
 *
 * Ohne `IntersectionObserver` (aeltere Browser, Testumgebungen) gilt alles
 * sofort als sichtbar. Das ist der richtige Ausfall: langsamer, aber
 * vollstaendig.
 */
function useInView(ref: { current: HTMLElement | null }): boolean {
  const [seen, setSeen] = useState(typeof IntersectionObserver === 'undefined');

  useEffect(() => {
    if (seen) return;
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setSeen(true);
          observer.disconnect();
        }
      },
      // Ein Stueck Vorlauf, damit die Miniatur fertig ist, bevor sie oben
      // hereinkommt.
      { rootMargin: '120px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref, seen]);

  return seen;
}
